import { useEffect, useRef, useState } from "react";
import { ExternalLink, Loader2, Upload, X, Film, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { Card, PageHeader, Btn, FieldLabel, inputCls, textareaCls } from "@/components/admin/ui";
import { uploadFile } from "@/components/admin/PhotoUploader";
import { POLICY_TITLES } from "@/pages/store/Policy";

const capturePoster = (path) =>
  new Promise((resolve, reject) => {
    const v = document.createElement("video");
    v.muted = true;
    v.playsInline = true;
    v.preload = "auto";
    v.src = fileUrl(path);
    v.onloadeddata = () => { v.currentTime = Math.min(0.5, (v.duration || 1) / 2); };
    v.onseeked = () => {
      const c = document.createElement("canvas");
      const scale = Math.min(1, 1920 / (v.videoWidth || 1920));
      c.width = Math.round(v.videoWidth * scale);
      c.height = Math.round(v.videoHeight * scale);
      c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
      c.toBlob((b) => (b ? resolve(new File([b], "hero-poster.jpg", { type: "image/jpeg" })) : reject(new Error("capture failed"))), "image/jpeg", 0.82);
    };
    v.onerror = () => reject(new Error("video load failed"));
  });

const MediaField = ({ label, value, onChange, kind, testid, onUploaded }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const path = await uploadFile(f);
      onChange(path);
      if (onUploaded) await onUploaded(path);
      toast.success(`${kind === "video" ? "Video" : "Image"} uploaded — click Save to publish it`);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return (
    <div data-testid={testid}>
      <FieldLabel>{label}</FieldLabel>
      <div className="flex items-start gap-3">
        <div className="w-40 aspect-video rounded-lg bg-zinc-900 overflow-hidden flex items-center justify-center flex-shrink-0">
          {value ? (kind === "video" ? <video src={fileUrl(value)} muted autoPlay loop playsInline className="w-full h-full object-cover" /> : <img src={fileUrl(value)} alt="" className="w-full h-full object-cover" />)
            : kind === "video" ? <Film className="text-zinc-600" /> : <ImageIcon className="text-zinc-600" />}
        </div>
        <div className="flex flex-col gap-2">
          <Btn variant="secondary" type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testid}-upload`}>
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {value ? "Replace" : "Upload"}
          </Btn>
          {value && <Btn variant="secondary" type="button" onClick={() => onChange("")} data-testid={`${testid}-remove`}><X size={15} /> Remove</Btn>}
        </div>
      </div>
      <input ref={ref} type="file" hidden accept={kind === "video" ? "video/mp4,video/webm,video/quicktime" : "image/*"} onChange={(e) => { pick(e.target.files[0]); e.target.value = ""; }} />
    </div>
  );
};

const Text = ({ label, k, s, set, hint, area, rows = 4 }) => (
  <label className="block">
    <FieldLabel hint={hint}>{label}</FieldLabel>
    {area ? <textarea rows={rows} className={textareaCls} value={s[k] || ""} onChange={(e) => set({ ...s, [k]: e.target.value })} data-testid={`store-${k.replace(/_/g, "-")}-input`} />
      : <input className={inputCls} value={s[k] || ""} onChange={(e) => set({ ...s, [k]: e.target.value })} data-testid={`store-${k.replace(/_/g, "-")}-input`} />}
  </label>
);

export default function OnlineStore() {
  const [s, set] = useState(null);
  const [policy, setPolicy] = useState("shipping");
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/admin/settings").then((r) => set(r.data));
    api.get("/admin/products").then((r) => setProducts(r.data.filter((p) => p.status === "published")));
  }, []);
  if (!s) return null;

  const autoPoster = async (videoPath) => {
    if (s.hero_poster) return;
    try {
      const poster = await uploadFile(await capturePoster(videoPath));
      set((prev) => ({ ...prev, hero_poster: poster }));
      toast.success("Cover image created from your video");
    } catch {
      toast.message("Tip: upload a cover image so the banner shows instantly while the video loads.");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", s);
      set({ ...s, ...data });
      toast.success("Store updated");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setSaving(false);
  };

  return (
    <div data-testid="admin-online-store-page">
      <PageHeader title="Online Store" subtitle="Edit what customers see on your website.">
        <a href="/" target="_blank" rel="noreferrer"><Btn variant="secondary" data-testid="store-view-button"><ExternalLink size={15} /> View store</Btn></a>
        <Btn onClick={save} disabled={saving} data-testid="store-save-button">{saving && <Loader2 size={15} className="animate-spin" />} Save</Btn>
      </PageHeader>
      <div className="space-y-4">
        <Card title="Homepage banner" description="The big section at the top of your homepage.">
          <div className="grid md:grid-cols-2 gap-6">
            <MediaField label="Background video" kind="video" value={s.hero_video} onChange={(v) => set((prev) => ({ ...prev, hero_video: v }))} onUploaded={autoPoster} testid="store-hero-video" />
            <MediaField label="Video cover image (shown while loading)" kind="image" value={s.hero_poster} onChange={(v) => set((prev) => ({ ...prev, hero_poster: v }))} testid="store-hero-poster" />
          </div>
          <p className="text-xs text-zinc-500 mt-3" data-testid="store-hero-video-hint">{s.hero_video ? "Your video plays silently on a seamless loop." : "No video uploaded yet — upload your 3D printer heart video here."} MP4 (H.264) works best on every device. Up to 150 MB; under 20 MB loads fastest. A cover image is created automatically from the first frame if you don't add one.</p>
          <div className="grid md:grid-cols-2 gap-4 mt-5">
            <Text label="Headline" k="hero_headline" s={s} set={set} />
            <Text label="Button text" k="hero_button" s={s} set={set} />
            <div className="md:col-span-2"><Text label="Short text under headline" hint="(optional)" k="hero_subtext" s={s} set={set} /></div>
          </div>
        </Card>
        <Card title="Announcement bar" description="A thin red bar at the very top of every page. Leave empty to hide.">
          <Text label="Message" k="announcement" s={s} set={set} />
        </Card>
        <Card title="Featured collection">
          <div className="grid md:grid-cols-2 gap-4">
            <Text label="Collection name" k="drop_title" s={s} set={set} />
            <Text label="Small label above it" hint="(optional)" k="drop_subtitle" s={s} set={set} />
          </div>
          <label className="block mt-4"><FieldLabel>Product shown on the homepage</FieldLabel>
            <select className={inputCls} value={s.homepage_product_id || ""} onChange={(e) => set({ ...s, homepage_product_id: e.target.value })} data-testid="store-homepage-product-select">
              <option value="">Automatic — first featured product</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
          <p className="text-xs text-zinc-500 mt-3">One product is shown on the homepage. Your full catalog is always in the shop. Only published products can be chosen.</p>
        </Card>
        <Card title="Contact & social" description="Shown on your contact page and footer.">
          <div className="grid md:grid-cols-2 gap-4">
            <Text label="Contact email" k="contact_email" s={s} set={set} />
            <Text label="Phone" hint="(optional)" k="contact_phone" s={s} set={set} />
            <Text label="Instagram link" hint="(optional)" k="instagram" s={s} set={set} />
            <Text label="TikTok link" hint="(optional)" k="tiktok" s={s} set={set} />
          </div>
        </Card>
        <Card title="Policies" description="Templates are included — replace anything in [brackets] with your details.">
          <div className="flex gap-1 flex-wrap mb-4">
            {Object.entries(POLICY_TITLES).map(([k, l]) => (
              <button key={k} type="button" onClick={() => setPolicy(k)} data-testid={`store-policy-tab-${k}`}
                className={`px-3 h-8 rounded-lg text-sm font-medium ${policy === k ? "bg-zinc-950 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"}`}>{l}</button>
            ))}
          </div>
          <textarea rows={14} className={`${textareaCls} font-mono text-[13px]`} value={s.policies?.[policy] || ""}
            onChange={(e) => set({ ...s, policies: { ...s.policies, [policy]: e.target.value } })} data-testid="store-policy-textarea" />
        </Card>
      </div>
    </div>
  );
}
